import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { apiOrchestrator } from 'services/apiService';

export interface UseBatchUserNamesResult {
  /** Map of userId to display name (null if not found or error) */
  nameMap: Map<string, string | null>;
  /** Whether the batch fetch is still loading */
  isLoading: boolean;
}

interface UserPersonBasic {
  first_name: string;
  last_name: string;
}

const fetchSingleUserName = async (userId: string): Promise<string | null> => {
  try {
    const res = await apiOrchestrator.get<UserPersonBasic>(
      `/users-person/${userId}`
    );

    if (res.data?.first_name) {
      return `${res.data.first_name} ${res.data.last_name || ''}`.trim();
    }

    return null;
  } catch {
    return null;
  }
};

const fetchBatchUserNames = async (
  userIds: string[]
): Promise<Map<string, string | null>> => {
  const results = await Promise.all(
    userIds.map(async userId => {
      const name = await fetchSingleUserName(userId);
      return { userId, name };
    })
  );

  const nameMap = new Map<string, string | null>();
  results.forEach(({ userId, name }) => {
    nameMap.set(userId, name);
  });

  return nameMap;
};

/**
 * Hook to batch fetch user display names.
 *
 * Follows the same pattern as useBatchUserAvatars to solve the N+1 problem.
 *
 * @param userIds - Array of user IDs to fetch names for
 * @returns Object with nameMap and loading state
 */
export const useBatchUserNames = (
  userIds: (string | null | undefined)[]
): UseBatchUserNamesResult => {
  const uniqueUserIds = useMemo(() => {
    const validIds = userIds.filter((id): id is string => !!id);
    return Array.from(new Set(validIds)).sort();
  }, [userIds]);

  const queryKey = useMemo(() => ['batchUserNames', uniqueUserIds.join(',')], [uniqueUserIds]);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => fetchBatchUserNames(uniqueUserIds),
    enabled: uniqueUserIds.length > 0,
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 30,
  });

  const nameMap = useMemo(() => {
    if (!data) {
      return new Map<string, string | null>();
    }
    return data;
  }, [data]);

  return {
    nameMap,
    isLoading: isLoading && uniqueUserIds.length > 0,
  };
};

export default useBatchUserNames;
